/**
 * Settings UI primitives — Card, Section, SettingsRow, Button, Input.
 *
 * Designed for the /perfil page. Strict token use only (no hex). Each
 * primitive is intentionally tiny so we can compose without dragging
 * in a full UI library.
 */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

// ─── Card / Section ────────────────────────────────────────────────

export function SettingsCard({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={`rounded-xl border border-border bg-bg-surface shadow-sm ${className}`}
    >
      {children}
    </div>
  )
}

export function SettingsSection({
  title,
  description,
  children,
  footer,
}: {
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <SettingsCard>
      <div className="border-b border-border px-6 py-4">
        <h2 className="text-base font-semibold text-fg-primary">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-fg-secondary">{description}</p>
        )}
      </div>
      <div className="px-6 py-5">{children}</div>
      {footer && (
        <div className="flex items-center justify-end gap-2 rounded-b-xl border-t border-border bg-bg-app/40 px-6 py-3">
          {footer}
        </div>
      )}
    </SettingsCard>
  )
}

// ─── Field row ─────────────────────────────────────────────────────

export function FieldRow({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-fg-muted">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-xs text-fg-muted">{hint}</p>}
    </label>
  )
}

// ─── Input ─────────────────────────────────────────────────────────

type InputProps = InputHTMLAttributes<HTMLInputElement>

export function TextInput({ className = '', ...rest }: InputProps) {
  return (
    <input
      {...rest}
      className={`w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary outline-none transition-colors placeholder:text-fg-muted focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    />
  )
}

// ─── Select ────────────────────────────────────────────────────────

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>

export function SelectInput({ className = '', children, ...rest }: SelectProps) {
  return (
    <select
      {...rest}
      className={`w-full rounded-md border border-border bg-bg-app px-3 py-2 text-sm text-fg-primary outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    >
      {children}
    </select>
  )
}

// ─── Button ────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  loading?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-r from-brand-500 to-rose-500 text-white shadow-sm hover:shadow-lg hover:shadow-brand-500/30',
  secondary:
    'border border-border bg-bg-surface text-fg-primary hover:bg-bg-elevated',
  danger:
    'border border-error/30 bg-error-tint text-error-fg hover:bg-error-tint/80',
  ghost:
    'text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary',
}

export function ActionButton({
  variant = 'primary',
  loading = false,
  className = '',
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50 ${variantClasses[variant]} ${className}`}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  )
}

// ─── Inline alert ──────────────────────────────────────────────────

export function InlineAlert({
  variant,
  children,
}: {
  variant: 'success' | 'error' | 'warning' | 'info'
  children: ReactNode
}) {
  const styles: Record<typeof variant, string> = {
    success: 'bg-success-tint text-success-fg',
    error: 'bg-error-tint text-error-fg',
    warning: 'bg-warning-tint text-warning-fg',
    info: 'bg-bg-elevated text-fg-secondary',
  }
  return (
    <div className={`rounded-md px-3 py-2 text-sm ${styles[variant]}`}>
      {children}
    </div>
  )
}

// ─── Definition list (read-only key/value rows) ────────────────────

export function DefList({ children }: { children: ReactNode }) {
  return <dl className="divide-y divide-border">{children}</dl>
}

export function DefRow({
  term,
  children,
}: {
  term: string
  children: ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
      <dt className="text-xs font-medium uppercase tracking-wider text-fg-muted">
        {term}
      </dt>
      <dd className="text-right text-sm text-fg-primary">{children}</dd>
    </div>
  )
}
